import axios from 'axios';
import config from '../config';

class AuthService {
    constructor() {
        if (AuthService.instance) {
            // console.log('AuthService.instance', this.access_token);
            return AuthService.instance;
        }

        this.axiosInstance = axios.create({
            baseURL: config.apiBaseUrl,
            withCredentials: true, // Important for cookies
            headers: {
                'Content-Type': 'application/json',
                // 'Access-Control-Allow-Origin': config.apiBaseUrl,
            },
        });

        // console.log('authService constructed');
        this.access_token = null;
        this.isRefreshing = false;
        this.failedQueue = [];
        this.refreshTokenExpired = false;

        this.setupInterceptors();

        AuthService.instance = this;
    }

    setupInterceptors() {
        // Request interceptor to add access token
        this.axiosInstance.interceptors.request.use(
            (config) => {
                // console.log('request', config);
                // console.log('request', this.access_token);
                // console.log('request', this.getAccessToken());

                if (!this.access_token) {
                    const storedUser = localStorage.getItem("user");
                    const profile = JSON.parse(storedUser);
                    const access_token = profile["access_token"];
                    this.access_token = access_token;
                    // console.log(this.access_token);
                }

                if (this.access_token) {
                    config.headers.Authorization = `Bearer ${this.access_token}`;
                }
                return config;
            },
            (error) => {
                // console.log('request', error);
                return Promise.reject(error);
            }
        );

        // Response interceptor to handle token refresh
        this.axiosInstance.interceptors.response.use(
            (response) => {
                // console.log('response', response);
                return response;
            },
            async (error) => {
                const originalRequest = error.config;
                // console.log('originalRequest', originalRequest);

                if (error.response?.status === 401 && !originalRequest._retry) {
                    if (this.isRefreshing) {
                        // console.log('response', 'already refreshing tokens, queue current request');
                        return new Promise((resolve, reject) => {
                            this.failedQueue.push({ resolve, reject });

                        }).then(token => {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                            return this.axiosInstance(originalRequest);

                        }).catch(err => {
                            return Promise.reject(err);
                        });
                    }

                    originalRequest._retry = true;
                    this.isRefreshing = true;

                    try {
                        // console.log('response', 'refreshing tokens ...');
                        const response = await this.refreshToken();
                        // console.log('refreshToken', response);

                        const new_access_token = response.data.access_token;
                        // console.log('new_access_token', new_access_token);
                        this.setAccessToken(new_access_token);
                        
                        // Process failed queue
                        this.processQueue(null, new_access_token);
                        
                        // Retry original request
                        originalRequest.headers.Authorization = `Bearer ${new_access_token}`;
                        return this.axiosInstance(originalRequest);

                    } catch (refreshError) {
                        // console.log('Failed to refresh tokens');
                        this.processQueue(refreshError, null);
                        this.clearTokens();
                        this.refreshTokenExpired = true;

                        // Redirect to login or dispatch logout action
                        // window.location.href = '/login';
                        return Promise.reject(refreshError);

                    } finally {
                        this.isRefreshing = false;
                    }
                }

                return Promise.reject(error);
            }
        );
    }

    processQueue(error, token = null) {
        this.failedQueue.forEach(({ resolve, reject }) => {
            if (error) {
                reject(error);
            } else {
                resolve(token);
            }
        });
        
        this.failedQueue = [];
    }

    getAccessToken() {
        return this.access_token;
    }

    setAccessToken(token) {
        // console.log(token);
        this.access_token = token;
        // console.log(this.access_token);
    }

    clearTokens() {
        this.access_token = null;
    }

    async refreshToken() {
        try {
            const response = await axios.get(`${config.apiBaseUrl}/auth/refresh`, {
                withCredentials: true,  // must include this to allow fastAPI to create cookies
                headers: {
                    'Content-Type': 'application/json',
                    // 'Access-Control-Allow-Origin': config.apiBaseUrl,
                },
            });
            // const response = await this.axiosInstance.get('/auth/refresh');
            // console.log(response);
            return response;

        } catch {
            // console.log('refreshToken failed');
        }
    }

    // Universal API call method
    async apiCall(method, url, data = null, config = {}) {
        try {
            const response = await this.axiosInstance({
                method,
                url,
                data,
                ...config,
            });
            // return response.data;
            return response;

        } catch (error) {
            throw this.handleApiError(error);
        }
    }

    // Convenience methods
    async get(url, config = {}) {
        return this.apiCall('GET', url, null, config);
    }

    async post(url, data, config = {}) {
        return this.apiCall('POST', url, data, config);
    }

    async put(url, data, config = {}) {
        return this.apiCall('PUT', url, data, config);
    }

    async delete(url, config = {}) {
        return this.apiCall('DELETE', url, null, config);
    }

    handleApiError(error) {
        if (error.response) {
            // Server responded with error status
            return {
                message: error.response.data?.detail || 'An error occurred',
                status: error.response.status,
                data: error.response.data,
            };
        } else if (error.request) {
            // Request was made but no response received
            return {
                message: 'Network error - no response received',
                status: null,
                data: null,
            };
        } else {
            // Something else happened
            return {
                message: error.message || 'An unexpected error occurred',
                status: null,
                data: null,
            };
        }
    }

    // Authentication methods
    async login(user) {
        const response = await this.post('/auth/login', user);
        this.setAccessToken(response.access_token);
        return response;
    }

    async register(email, password) {
        return this.post('/auth/register', { email, password });
    }

    async logout() {
        try {
            await this.post('/auth/logout');
        } catch (error) {
            // console.error('Logout error:', error);
        } finally {
            this.clearTokens();
        }
    }

    async getCurrentUser() {
        return this.get('/auth/me');
    }

    async checkAuthStatus() {
        try {
            if (this.access_token) {
                // Try to get current user with existing token
                const user = await this.getCurrentUser();
                return { isAuthenticated: true, user };
            } else {
                // Try to refresh token
                const response = await this.refreshToken();
                this.setAccessToken(response.data.access_token);
                const user = await this.getCurrentUser();
                return { isAuthenticated: true, user };
            }
        } catch (error) {
            this.clearTokens();
            return { isAuthenticated: false, user: null };
        }
    }
}

// Create singleton instance
const authService = new AuthService();

export default authService;

